import { CircularProgress, IconButton, makeStyles } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {
  boschGray,
  boschRed,
} from '../../../modules/theme/boschColors';

const useStyles = makeStyles(theme => ({
  /*
   * Slim 24px-tall strip that lives at the bottom of every `api-*`
   * widget. We keep the chrome small on purpose — the widget's
   * data is the star; this row exists for "when did this last update,
   * and is the API healthy?" signals.
   */
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.25),
    borderTop: `1px solid ${boschGray[90]}`,
    backgroundColor: boschGray[95],
    fontSize: '0.7rem',
    color: boschGray[40],
    fontWeight: 500,
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  errorRow: {
    color: boschRed[40],
    fontWeight: 600,
  },
  inlineSpinner: {
    color: boschGray[40],
  },
  refresh: {
    padding: 4,
    color: boschGray[40],
  },
}));

export type ApiStatusBarProps = {
  loading: boolean;
  error: Error | undefined;
  lastUpdatedAt: Date | undefined;
  /** Optional manual refresh handler. When omitted the icon button
   *  is hidden — useful for inline-JSON widgets where "refresh"
   *  is meaningless. */
  onRefresh?: () => void;
  /** Override the "Live" / "Static" status label. Default: derived
   *  from whether `onRefresh` was supplied. */
  modeLabel?: string;
};

/**
 * Tiny footer for `api-*` widgets that reports the data freshness
 * + any current error. We deliberately don't wrap the data area with
 * an error boundary — the widget body should still render the last
 * good state when a refresh fails, and the error banner here is the
 * single source of truth for "something's wrong with the upstream".
 *
 * Showing the FULL error message inline (and not just a generic
 * "Failed to load") is intentional: an admin debugging a misconfigured
 * proxy reads `HTTP 401 Unauthorized — /api/proxy/jira/...` and
 * immediately knows where to look, whereas "Failed to load" sends
 * them digging through devtools.
 */
export const ApiStatusBar = ({
  loading,
  error,
  lastUpdatedAt,
  onRefresh,
  modeLabel,
}: ApiStatusBarProps) => {
  const classes = useStyles();
  const mode = modeLabel ?? (onRefresh ? 'Live' : 'Static');

  if (error) {
    return (
      <div className={`${classes.root} ${classes.errorRow}`}>
        <span className={classes.left}>
          <ErrorOutlineIcon style={{ fontSize: 14 }} />
          <span>{error.message}</span>
        </span>
        {onRefresh && (
          <IconButton
            className={classes.refresh}
            onClick={onRefresh}
            title="Retry"
          >
            <RefreshIcon style={{ fontSize: 16 }} />
          </IconButton>
        )}
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <span className={classes.left}>
        {loading ? (
          <>
            <CircularProgress
              size={10}
              className={classes.inlineSpinner}
            />
            <span>Fetching…</span>
          </>
        ) : (
          <span>
            {mode}
            {lastUpdatedAt && (
              <>
                {' · last updated '}
                {lastUpdatedAt.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}
              </>
            )}
          </span>
        )}
      </span>
      {onRefresh && (
        <IconButton
          className={classes.refresh}
          onClick={onRefresh}
          title="Refresh now"
          disabled={loading}
        >
          <RefreshIcon style={{ fontSize: 16 }} />
        </IconButton>
      )}
    </div>
  );
};
